import { useState } from 'react';
import { useLocation } from 'wouter';
import { motion } from 'framer-motion';
import {
  LayoutGrid,
  Copy,
  Trash2,
  ExternalLink,
  ToggleLeft,
  ToggleRight,
  Globe,
  Pencil,
  Plus,
} from 'lucide-react';
import {
  useAdminLandingVariants,
  useDeleteLandingVariant,
  useUpdateLandingVariant
} from '@/lib/api/useLandingVariant';
import { useToast } from '@/components/ux/Toast';
import type { LandingVariant } from '@shared/types/landingVariant';
import { Loader } from '@/components/Loader';

export default function AdminLandingVariants() {
  const { data, isLoading } = useAdminLandingVariants();
  const deleteMutation = useDeleteLandingVariant();
  const updateMutation = useUpdateLandingVariant();
  const toast = useToast();
  const [, navigate] = useLocation();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const variants: LandingVariant[] = data?.data || [];

  const toggleActive = async (e: React.MouseEvent, variant: LandingVariant) => {
    e.stopPropagation();
    try {
      await updateMutation.mutateAsync({
        id: variant.id,
        isActive: !variant.isActive
      });
      toast.showToast(`${variant.name} ${variant.isActive ? 'deactivated' : 'activated'}`, 'success');
    } catch {
      toast.showToast('Failed to update variant', 'error');
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await deleteMutation.mutateAsync(id);
      setConfirmDeleteId(null);
      toast.showToast('Variant deleted', 'success');
    } catch {
      toast.showToast('Failed to delete variant', 'error');
    }
  };

  const copyConfig = (e: React.MouseEvent, variant: LandingVariant) => {
    e.stopPropagation();
    const payload = {
      slug: variant.slug + '-copy',
      name: variant.name + ' (Copy)',
      language: variant.language,
      isActive: false,
      config: variant.config,
    };
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    toast.showToast('Config JSON copied to clipboard', 'success');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <LayoutGrid className="w-6 h-6 text-primary-500" />
          <h1 className="text-2xl font-bold dark:text-neutral-100">Landing Page Variants</h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-dark-muted dark:text-neutral-400">
            {variants.length} variant{variants.length !== 1 ? 's' : ''}
          </span>
          <button
            onClick={() => navigate('/landing-variants/new')}
            className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors"
          >
            <Plus className="w-4 h-4" /> New Variant
          </button>
        </div>
      </div>

      <p className="text-sm text-dark-muted dark:text-neutral-400 mb-6">
        Database-driven landing pages for Meta ad campaigns. Click a variant to edit its configuration.
      </p>

      {variants.length === 0 ? (
        <div className="card p-12 text-center">
          <LayoutGrid className="w-12 h-12 text-dark-muted dark:text-neutral-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold dark:text-neutral-100 mb-2">No variants yet</h3>
          <p className="text-dark-muted dark:text-neutral-400 mb-4">
            Create your first landing page variant to get started.
          </p>
          <button
            onClick={() => navigate('/landing-variants/new')}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors"
          >
            <Plus className="w-4 h-4" /> Create Variant
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-200 dark:border-neutral-700">
                <th className="text-left py-3 px-4 text-sm font-medium text-dark-muted dark:text-neutral-400">Name</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-dark-muted dark:text-neutral-400">Slug</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-dark-muted dark:text-neutral-400">Language</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-dark-muted dark:text-neutral-400">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-dark-muted dark:text-neutral-400">Created</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-dark-muted dark:text-neutral-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {variants.map((variant) => {
                const previewSlug = variant.slug;
                return (
                  <tr
                    key={variant.id}
                    onClick={() => navigate(`/landing-variants/${variant.id}`)}
                    className="border-b border-neutral-100 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors cursor-pointer"
                  >
                    <td className="py-3 px-4">
                      <div className="font-medium dark:text-neutral-100">{variant.name}</div>
                      <div className="text-xs text-dark-muted dark:text-neutral-500">
                        {variant.config.posthogEvent}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <code className="text-sm bg-neutral-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                        ?v={previewSlug}
                      </code>
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center gap-1.5 text-sm">
                        <Globe className="w-3.5 h-3.5" />
                        {variant.language.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={(e) => toggleActive(e, variant)}
                        className="inline-flex items-center gap-1.5 text-sm cursor-pointer"
                        disabled={updateMutation.isPending}
                      >
                        {variant.isActive ? (
                          <>
                            <ToggleRight className="w-5 h-5 text-emerald-500" />
                            <span className="text-emerald-600 dark:text-emerald-400">Active</span>
                          </>
                        ) : (
                          <>
                            <ToggleLeft className="w-5 h-5 text-neutral-400" />
                            <span className="text-neutral-500">Inactive</span>
                          </>
                        )}
                      </button>
                    </td>
                    <td className="py-3 px-4 text-sm text-dark-muted dark:text-neutral-400">
                      {new Date(variant.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/landing-variants/${variant.id}`); }}
                          className="p-1.5 rounded hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4 text-dark-muted dark:text-neutral-400" />
                        </button>
                        <a
                          href={`https://outclose.com/?v=${previewSlug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="p-1.5 rounded hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                          title="Preview"
                        >
                          <ExternalLink className="w-4 h-4 text-dark-muted dark:text-neutral-400" />
                        </a>
                        <button
                          onClick={(e) => copyConfig(e, variant)}
                          className="p-1.5 rounded hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                          title="Copy config JSON"
                        >
                          <Copy className="w-4 h-4 text-dark-muted dark:text-neutral-400" />
                        </button>
                        {confirmDeleteId === variant.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => handleDelete(e, variant.id)}
                              className="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                              disabled={deleteMutation.isPending}
                            >
                              Confirm
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                              className="text-xs px-2 py-1 bg-neutral-200 dark:bg-neutral-700 rounded hover:bg-neutral-300 dark:hover:bg-neutral-600 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(variant.id); }}
                            className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
}
